import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Select,
  DatePicker,
  Modal,
  Table,
  Empty,
  Button,
  Row,
  Col,
  message,
  Tooltip as AntTooltip,
} from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  HomeOutlined,
  SafetyOutlined,
  TeamOutlined,
  SwapOutlined,
  LoginOutlined,
  LogoutOutlined,
  FileProtectOutlined,
  CheckOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  PieChartOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import moment from "moment";
import domtoimage from "dom-to-image-more";
import * as XLSX from "xlsx-js-style";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { adminService } from "../../_services/admin.service";
import {
  getDefaultReportFooter,
  getReportDateRangeText,
  getReportHeaderText,
} from "../../_helpers/downloadReportPdf";
import "./adminNewDashboard.css";
import { ViewFormModel } from "../ProfessionalForm/ViewForm/ViewFormModel";
import { professionalAction } from "../../_actions";
import { ViewGunaDetails } from "../viewGunaDetails/viewGunaDetails";
import { LanguageContext } from "../../context/LanguageContext";

const { RangePicker } = DatePicker;
const { Option } = Select;

/** Section labels aligned with dashboard design (screenshot) */
const MOVEMENT_SECTIONS = [
  {
    key: "local",
    position: "tl",
    tone: "blue",
    Icon: HomeOutlined,
    titleMr: "हद्दीत राहून हद्दीत गुन्हे करणारे",
    titleEn: "LOCAL CRIMINALS",
    subtitle: (ps) =>
      `(${ps} पो.स्टे. हद्दीत राहून त्याच हद्दीत गुन्हे करणारे)`,
    countKey: "localCount",
  },
  {
    key: "internal",
    position: "tr",
    tone: "green",
    Icon: SwapOutlined,
    titleMr: "एका पो.स्टे. मधून दुसऱ्या पो.स्टे. हद्दीत गुन्हे करणारे आरोपी",
    titleEn: "INTERNAL MOVEMENT",
    subtitle: (ps) => `(${ps} पो.स्टे. मधील एका हद्दीतून दुसऱ्या हद्दीत)`,
    countKey: "internalCount",
  },
  {
    key: "external_incoming",
    position: "bl",
    tone: "purple",
    Icon: LoginOutlined,
    titleMr: "बाहेरील जिल्हा/घटकातून आपल्या हद्दीत येणारे आरोपी",
    titleEn: "EXTERNAL INCOMING",
    subtitle: (ps) =>
      `(बाहेरील हद्दीतून आपल्या ${ps} पो.स्टे. मध्ये गुन्हे करणारे)`,
    countKey: "externalIncoming",
  },
  {
    key: "external_outgoing",
    position: "br",
    tone: "orange",
    Icon: LogoutOutlined,
    titleMr: "आपल्या हद्दीतील आरोपी बाहेरील जिल्हा/घटकात गुन्हे करणारे",
    titleEn: "EXTERNAL OUTGOING",
    subtitle: (ps) =>
      `(आपल्या ${ps} पो.स्टे. मधील आरोपी बाहेरील हद्दीत गुन्हे करणारे)`,
    countKey: "externalOutgoing",
  },
];

const PIE_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const MOVEMENT_MODAL_CONFIG = Object.fromEntries(
  MOVEMENT_SECTIONS.map((s) => [
    s.key,
    {
      titleMr: s.titleMr,
      titleEn: s.titleEn,
    },
  ]),
);

const formatNumber = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString("en-IN");
};

/** district_name(city name) — e.g. कळवा(ठाणे-शहर) */
const formatDistrictWithCity = (district, city) => {
  const d = (district && String(district).trim()) || "—";
  const c = city && String(city).trim();
  if (!c) return d;
  return `${d}/${c}`;
};

const formatPercent = (value, total) => {
  const v = Number(value);
  const t = Number(total);
  if (!t || !Number.isFinite(v)) return "0.00%";
  return `${((v / t) * 100).toFixed(2)}%`;
};

const formatDateRange = (dates) => {
  if (!dates?.[0] || !dates?.[1]) return "";
  return `${moment(dates[0]).format("DD/MM/YYYY")} - ${moment(dates[1]).format("DD/MM/YYYY")}`;
};

/** map_cities is a JSON array of city ids, e.g. [1, 2, 3] */
const parseMapCityIds = (raw) => {
  let list = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        list = JSON.parse(trimmed);
      } catch (e) {
        list = trimmed.split(",");
      }
    } else {
      list = trimmed.split(",");
    }
  }
  if (!Array.isArray(list)) {
    if (list && typeof list === "object") list = [list];
    else return [];
  }
  return list
    .map((item) => {
      if (item && typeof item === "object") {
        return Number(item.id ?? item.cityId ?? item.city_id);
      }
      return Number(String(item).trim());
    })
    .filter((n) => Number.isFinite(n));
};

const CMIS_LOADER_STYLES = `
.admin-nd-page {
  position: relative;
}
.cmis-verification-loader {
  position: absolute;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(244, 246, 251, 0.55);
  backdrop-filter: blur(6px);
  box-sizing: border-box;
}
.cmis-verification-loader__panel {
  width: min(520px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px 28px;
}
.cmis-loader-scope {
  position: relative;
  min-height: 280px;
}
.cmis-verification-loader--modal {
  position: absolute;
  inset: 0;
  z-index: 100;
  border-radius: 8px;
  background: rgba(244, 246, 251, 0.72);
  backdrop-filter: blur(6px);
}
.cmis-verification-loader__ring-wrap {
  position: relative;
  width: 96px;
  height: 96px;
  margin-bottom: 22px;
}
.cmis-verification-loader__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: cmisVerificationLoaderRing 1.05s linear infinite;
  background: conic-gradient(
    #1565c0 0deg 80deg,
    #dce8f7 80deg 170deg,
    #dce8f7 170deg 260deg,
    #dce8f7 260deg 360deg
  );
  -webkit-mask: radial-gradient(
    farthest-side,
    transparent calc(100% - 4px),
    #000 calc(100% - 4px)
  );
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - 4px),
    #000 calc(100% - 4px)
  );
}
.cmis-verification-loader__icon-wrap {
  position: absolute;
  inset: 14px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 10px 30px rgba(13, 71, 161, 0.18),
    0 0 0 1px rgba(21, 101, 192, 0.10) inset;
  animation: cmisVerificationLoaderPulse 1.6s ease-in-out infinite;
}
.cmis-verification-loader__icon {
  font-size: 54px;
  line-height: 1;
  color: #1565c0;
}
.cmis-verification-loader__title {
  margin: 2px 0 10px;
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #1a2b3b;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.75);
}
.cmis-verification-loader__status {
  margin: 0 0 8px;
  font-size: 1.12rem;
  font-weight: 700;
  color: #2f3f52;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.75);
}
.cmis-verification-loader__hint {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 600;
  color: #4f6278;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.75);
}
@keyframes cmisVerificationLoaderRing {
  to {
    transform: rotate(360deg);
  }
}
@keyframes cmisVerificationLoaderPulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow:
      0 10px 30px rgba(13, 71, 161, 0.18),
      0 0 0 1px rgba(21, 101, 192, 0.10) inset;
  }
  50% {
    transform: scale(1.03);
    box-shadow:
      0 14px 38px rgba(13, 71, 161, 0.22),
      0 0 0 1px rgba(21, 101, 192, 0.12) inset;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cmis-verification-loader__ring,
  .cmis-verification-loader__icon-wrap {
    animation: none !important;
  }
}
`;

class AdminNewDashboard extends Component {
  static contextType = LanguageContext;
  state = {
    main_head: [],
    advancedFilterTitle: "मोडस",
    crimeTitle: ["all"],
    selectedHeadings: [],
    kayda: null,
    kalam: [],
    dateFilter: "1month",
    customDates: [moment().subtract(1, "month"), moment()],
    loading: true,
    list: null,
    detailModalVisible: false,
    detailModalType: null,
    detailModalView: "list",
    detailLoading: false,
    detailAccusedList: [],
    detailTop5: [],
    detailDistrictChartLimit: "5",
    detailModusPie: [],
    detailSelectedDistrictId: null,
    detailSelectedDistrictName: null,
    detailDistrictAccusedList: [],
    psDistrictModalVisible: false,
    psDistrictPagination: { current: 1, pageSize: 15 },
    movementDetailPagination: { current: 1, pageSize: 10 },
    showGunaDetails: false,
    gunaLoading: false,
    selectedCityId: (() => {
      const role = String(this.props.user?.role ?? "");
      if (role !== "12") return null;
      const ids = parseMapCityIds(this.props.user?.map_cities);
      return ids.length ? ids[0] : null;
    })(),
  };

  showGunaViewModel = () => {
    this.setState({
      showGunaDetails: true,
      detailModalVisible: false,
    });
  };

  closeGunaViewModel = () => {
    this.setState({
      showGunaDetails: false,
      detailModalVisible: true,
    });
  };

  onGunaForm = (data) => {
    if (data) {
      this.setState({ gunaLoading: true });
      this.showGunaViewModel();
      this.props.dispatch(professionalAction.getCriminalsViewsById(data));
    }
  };
  closeCriminalViewModel = () => {
    this.setState({
      setCriminalViewModel: false,
      detailModalVisible: true,
    });
  };

  showCriminalViewModel = () => {
    this.setState({
      setCriminalViewModel: true,
      detailModalVisible: false,
    });
  };

  onViewAccusedForm = (data) => {
    if (data) {
      this.showCriminalViewModel();
      this.props.dispatch(professionalAction.getCriminalsViewsById(data));
    }
  };

  componentDidMount() {
    this.fetchDashboard();
    this.props.dispatch(professionalAction.getAllMaster());
    this.props.dispatch(professionalAction.getCity());

    this.props.dispatch(professionalAction.getDharm());
    this.props.dispatch(professionalAction.getKalam());
    this.props.dispatch(professionalAction.getKayda());
    this.props.dispatch(professionalAction.getCrimeType());
    this.props.dispatch(professionalAction.getCrimeTitle());
    this.props.dispatch(professionalAction.getStatus());
    this.getMainHead();
  }

  renderCmisLoader = ({ variant } = {}) => (
    <div
      className={`cmis-verification-loader${
        variant === "modal" ? " cmis-verification-loader--modal" : ""
      }`}
      role="status"
      aria-live="polite"
      aria-label="Loading dashboard"
    >
      <div className="cmis-verification-loader__panel">
        <div className="cmis-verification-loader__ring-wrap">
          <div className="cmis-verification-loader__ring" aria-hidden="true" />
          <div
            className="cmis-verification-loader__icon-wrap"
            aria-hidden="true"
          >
            <SafetyOutlined className="cmis-verification-loader__icon" />
          </div>
        </div>
        <h2 className="cmis-verification-loader__title">CMIS</h2>
        <p className="cmis-verification-loader__status">Verifying Records...</p>
        <p className="cmis-verification-loader__hint">
          Please wait while we process your request
        </p>
      </div>
    </div>
  );

  getMainHead = () => {
    professionalAction.getMainHead().then((res) => {
      this.setState({
        main_head: res.data,
      });
    });
  };

  handleKala = (e) => {
    this.setState({
      kayda: e,
      kalam: [],
    });
    this.props.dispatch(professionalAction.getKalam(e));
  };

  handleAdvancedFilterTitleChange = (value) => {
    this.setState({
      advancedFilterTitle: value,
      crimeTitle: ["all"],
      selectedHeadings: [],
      kayda: null,
      kalam: [],
    });
  };

  handleCrimeChange = (value) => {
    let updated = value.map(String);

    if (updated.includes("all") && updated.length > 1) {
      updated = updated.filter((v) => v !== "all");
    }

    if (updated.length === 0) {
      updated = ["all"];
    }

    this.setState({
      crimeTitle: updated,
    });
  };

  componentDidUpdate(prevProps, prevState) {
    // Check if the data has just arrived
    const prevList = prevProps.professionals?.criminalViewList?.gunhaInfor;
    const currList = this.props.professionals?.criminalViewList?.gunhaInfor;
    if (this.state.gunaLoading && prevList !== currList) {
      this.setState({ gunaLoading: false });
    }
    const prevPrevetive = prevProps.professionals?.criminalViewList;
    const currtPrevetive = this.props.professionals?.criminalViewList;
    if (this.state.preventiveLoading && prevPrevetive !== currtPrevetive) {
      this.setState({ preventiveLoading: false });
    }
  }
  handleDateFilterChange = (value) => {
    let dates = this.state.customDates;

    if (value === "1month") {
      dates = [moment().subtract(1, "month"), moment()];
    } else if (value === "6month") {
      dates = [moment().subtract(6, "month"), moment()];
    } else if (value === "1year") {
      dates = [moment().subtract(1, "year"), moment()];
    }

    this.setState({ dateFilter: value, customDates: dates });
  };

  isOfficerRole = () => {
    const role = String(this.props.user?.role ?? "");
    return role === "9" || role === "10";
  };

  isMappedCityRole = () => String(this.props.user?.role ?? "") === "12";

  getAllocatedCities = () => {
    const ids = parseMapCityIds(this.props.user?.map_cities);
    const pcCities = this.props.professionals?.pc_cities || [];
    return ids.map((id) => {
      const match = pcCities.find((city) => Number(city.id) === id);
      return {
        id,
        name: match?.City || match?.city || match?.name || String(id),
      };
    });
  };

  handleMappedCityChange = (cityId) => {
    this.setState({ selectedCityId: Number(cityId) }, () =>
      this.fetchDashboard(),
    );
  };

  getMappedDistrictIds = () => {
    const raw = this.props.user?.map_districtId;
    if (Array.isArray(raw)) {
      return raw
        .filter((id) => id !== null && id !== undefined && id !== "")
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));
    }
    if (raw !== null && raw !== undefined && raw !== "") {
      if (typeof raw === "string" && raw.trim().startsWith("[")) {
        try {
          return JSON.parse(raw)
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n));
        } catch (e) {
          /* fall through */
        }
      }
      return String(raw)
        .split(",")
        .map((id) => Number(String(id).trim()))
        .filter((n) => Number.isFinite(n));
    }
    return [];
  };

  getRequestBody = () => {
    const { user } = this.props;
    const {
      customDates,
      advancedFilterTitle,
      crimeTitle,
      selectedHeadings,
      kayda,
      kalam,
    } = this.state;
    const mappedCityId = this.isMappedCityRole()
      ? (this.state.selectedCityId ??
        parseMapCityIds(user?.map_cities)[0] ??
        null)
      : null;
    const cityId = this.isMappedCityRole() ? mappedCityId : user?.cityId;
    const body = {
      cityId,
      city: cityId,
      startDate: moment(customDates[0]).format("YYYY-MM-DD"),
      endDate: moment(customDates[1]).format("YYYY-MM-DD"),
      title: advancedFilterTitle,
      heading:
        advancedFilterTitle === "मोडस"
          ? crimeTitle === "all"
            ? null
            : crimeTitle
          : advancedFilterTitle === "शीर्षक"
            ? Array.isArray(selectedHeadings) &&
              selectedHeadings.length > 0 &&
              selectedHeadings[0].toString() !== "all"
              ? selectedHeadings
              : null
            : advancedFilterTitle === "लघु शीर्षक"
              ? Array.isArray(crimeTitle) &&
                crimeTitle.length > 0 &&
                crimeTitle[0].toString() !== "all"
                ? crimeTitle
                : null
              : null,
      kayda: advancedFilterTitle === "कायदा" ? kayda : null,
      kalam:
        advancedFilterTitle === "कायदा"
          ? Array.isArray(kalam) &&
            kalam.length > 0 &&
            kalam[0].toString() !== "all"
            ? kalam
            : null
          : null,
    };
    if (this.isOfficerRole()) {
      const mappedDistrictIds = this.getMappedDistrictIds();
      body.map_districtId = mappedDistrictIds;
      body.districtIds = mappedDistrictIds;
      body.userid = user?.id;
    }
    return body;
  };

  getDashboardApi = () =>
    this.isOfficerRole()
      ? adminService.RoleWiseDashboard
      : adminService.AdminNewDashboard;

  getDashboardDetailApi = () =>
    this.isOfficerRole()
      ? adminService.RoleWiseDashboardAccusedDetail
      : adminService.AdminNewDashboardAccusedDetail;

  fetchDashboard = () => {
    if (this.isOfficerRole() && this.getMappedDistrictIds().length === 0) {
      this.setState({
        loading: false,
        list: {
          total_police_stations: 0,
          police_station_count: 0,
          district_names: [],
        },
      });
      message.warning("Mapped police stations not found");
      return;
    }
    if (this.isMappedCityRole()) {
      const cityId =
        this.state.selectedCityId ??
        parseMapCityIds(this.props.user?.map_cities)[0];
      if (cityId == null) {
        this.setState({ loading: false, list: null });
        message.warning("Allocated cities not found");
        return;
      }
    }
    const payload = this.getRequestBody();
    this.setState({ loading: true });
    this.getDashboardApi()(payload)
      .then((res) => {
        this.setState({ list: res?.data ?? res });
      })
      .catch(() => {
        this.setState({ list: null });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  getMetrics = () => {
    const list = this.state.list || {};
    const totalAccused = Number(
      list.total_accused ?? list.totalAccused ?? list.total_accused_count ?? 0,
    );
    const localCount = Number(
      list.local_count ??
        list.localCriminalsCount ??
        list.local_criminals_count ??
        list.same_count ??
        0,
    );
    const internalCount = Number(
      list.internal_movement_count ??
        list.internalMovementCount ??
        list.internal_count ??
        0,
    );
    const externalIncoming = Number(
      list.external_incoming_count ??
        list.externalIncomingCount ??
        list.inside_count ??
        0,
    );
    const externalOutgoing = Number(
      list.external_outgoing_count ??
        list.externalOutgoingCount ??
        list.outsider_count ??
        0,
    );
    let totalPoliceStations = Number(
      list.total_police_stations ??
        list.policeStationCount ??
        list.total_ps ??
        0,
    );
    if (this.isOfficerRole()) {
      totalPoliceStations = this.getMappedDistrictIds().length;
    }
    const totalCrimes = Number(
      list.total_crimes ?? list.totalCrimeCount ?? list.total_crime_count ?? 0,
    );
    let cityName = list.cityName || list.city_name || "";
    if (this.isMappedCityRole()) {
      const selected = this.getAllocatedCities().find(
        (city) => city.id === Number(this.state.selectedCityId),
      );
      if (selected?.name) cityName = selected.name;
    }
    const computedTotal =
      totalAccused ||
      localCount + internalCount + externalIncoming + externalOutgoing;

    return {
      totalAccused: computedTotal,
      localCount,
      internalCount,
      externalIncoming,
      externalOutgoing,
      totalPoliceStations,
      totalCrimes,
      cityName,
      titleCount: totalPoliceStations || list.police_station_count || "—",
    };
  };

  openMovementDetailModal = (type) => {
    this.setState({
      detailModalVisible: true,
      detailModalType: type,
      detailModalView: "list",
      detailLoading: true,
      detailAccusedList: [],
      detailTop5: [],
      detailDistrictChartLimit: "5",
      detailModusPie: [],
      detailSelectedDistrictId: null,
      detailSelectedDistrictName: null,
      detailDistrictAccusedList: [],
      movementDetailPagination: { current: 1, pageSize: 10 },
    });

    const payload = {
      ...this.getRequestBody(),
      movementType: type,
    };

    this.getDashboardDetailApi()(payload)
      .then((res) => {
        const data = res?.data ?? res;
        this.setState({
          detailAccusedList: data?.accused_list || [],
          detailTop5: data?.top5_by_district || [],
          detailModusPie: data?.modus_pie || [],
          detailDistrictAccusedList: [],
          detailLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          detailAccusedList: [],
          detailTop5: [],
          detailModusPie: [],
          detailDistrictAccusedList: [],
          detailLoading: false,
        });
      });
  };

  fetchMovementModusPie = ({ districtId } = {}) => {
    const { detailModalType } = this.state;
    if (!detailModalType) return;

    this.setState({
      detailSelectedDistrictId: districtId ?? null,
      detailSelectedDistrictName:
        districtId == null ? null : this.state.detailSelectedDistrictName,
    });

    const payload = {
      ...this.getRequestBody(),
      movementType: detailModalType,
      ...(districtId != null ? { districtId } : {}),
    };

    this.getDashboardDetailApi()(payload)
      .then((res) => {
        const data = res?.data ?? res;
        this.setState({
          detailModusPie: data?.modus_pie || [],
          detailDistrictAccusedList:
            districtId != null ? data?.accused_list || [] : [],
        });
      })
      .catch(() => {
        this.setState({
          detailModusPie: [],
          detailDistrictAccusedList: [],
        });
      });
  };

  closeMovementDetailModal = () => {
    this.setState({
      detailModalVisible: false,
      detailModalType: null,
    });
  };

  getDistrictNamesList = () => {
    const list = this.state.list || {};
    const rows = Array.isArray(list.district_names) ? list.district_names : [];
    return rows.map((row, index) => ({
      sr_no: row.sr_no ?? index + 1,
      district_id: row.district_id ?? row.id,
      district_name:
        row.district_name ?? row.DistrictName ?? row.districtName ?? "—",
    }));
  };

  openPsDistrictModal = () => {
    this.setState({
      psDistrictModalVisible: true,
      psDistrictPagination: { current: 1, pageSize: 15 },
    });
  };

  closePsDistrictModal = () => {
    this.setState({ psDistrictModalVisible: false });
  };

  handlePsDistrictTableChange = (pagination) => {
    this.setState({
      psDistrictPagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    });
  };

  handleMovementDetailTableChange = (pagination) => {
    this.setState({
      movementDetailPagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    });
  };

  renderPsDistrictModal = () => {
    const { psDistrictModalVisible, psDistrictPagination } = this.state;
    const districts = this.getDistrictNamesList();
    const metrics = this.getMetrics();
    const psLabel = metrics.totalPoliceStations
      ? formatNumber(metrics.totalPoliceStations)
      : "—";

    const districtColumns = [
      {
        title: "अ.क्र.",
        dataIndex: "sr_no",
        width: 70,
        align: "center",
      },
      {
        title: "पोलीस स्टेशन / जिल्हा",
        dataIndex: "district_name",
      },
    ];

    return (
      <Modal
        title={`एकूण पो.स्टे. (${psLabel}) — पोलीस स्टेशन यादी`}
        visible={psDistrictModalVisible}
        onCancel={this.closePsDistrictModal}
        footer={null}
        width="95%"
        destroyOnClose
        className="admin-nd-ps-modal"
        bodyStyle={{
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {districts.length > 0 ? (
          <Table
            rowKey={(r) => String(r.sr_no)}
            columns={districtColumns}
            dataSource={districts}
            pagination={{
              current: psDistrictPagination.current,
              pageSize: psDistrictPagination.pageSize,
              total: districts.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "15", "20", "50"],
              hideOnSinglePage: true,
            }}
            onChange={this.handlePsDistrictTableChange}
            scroll={{ y: 420 }}
            bordered
            size="small"
          />
        ) : (
          <Empty description="पोलीस स्टेशन यादी उपलब्ध नाही" />
        )}
      </Modal>
    );
  };

  renderSectionCard = (sectionKey, metrics, totalAccused, psLabel) => {
    const section = MOVEMENT_SECTIONS.find((s) => s.key === sectionKey);
    if (!section) return null;
    const SectionIcon = section.Icon;
    const psForSubtitle = psLabel !== "—" ? psLabel : "—";
    return this.renderMovementCard({
      position: section.position,
      tone: section.tone,
      icon: <SectionIcon />,
      titleMr: section.titleMr,
      titleEn: section.titleEn,
      count: metrics[section.countKey],
      subtitle: section.subtitle(psForSubtitle),
      percent: formatPercent(metrics[section.countKey], totalAccused),
      movementType: section.key,
    });
  };

  renderMovementCard = ({
    position,
    tone,
    icon,
    titleMr,
    titleEn,
    count,
    subtitle,
    percent,
    movementType,
  }) => (
    <div
      role="button"
      tabIndex={0}
      className={`admin-nd-card admin-nd-card--clickable admin-nd-card--${position} admin-nd-card--${tone}`}
      onClick={() => this.openMovementDetailModal(movementType)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.openMovementDetailModal(movementType);
        }
      }}
    >
      <div className={`admin-nd-card__icon admin-nd-card__icon--${tone}`}>
        {icon}
      </div>
      <div className="admin-nd-card__body">
        <p className="admin-nd-card__title-mr">{titleMr}</p>
        <p className="admin-nd-card__title-en">{titleEn}</p>
        <h2 className="admin-nd-card__count">{formatNumber(count)}</h2>
        <p className="admin-nd-card__subtitle">{subtitle}</p>
        <div className={`admin-nd-card__pill admin-nd-card__pill--${tone}`}>
          <span className="admin-nd-card__pill-label">एकूण टक्केवारी</span>
          <span className="admin-nd-card__pill-value">{percent}</span>
        </div>
        <p className="admin-nd-card__hint">तपशील पाहण्यासाठी क्लिक करा</p>
      </div>
    </div>
  );

  renderSinglePieSection = (data, title) => {
    if (!data || data.length === 0) {
      return (
        <div style={{ width: "100%", textAlign: "center", padding: 24 }}>
          <h3 style={{ marginBottom: 12, color: "#1890ff" }}>{title}</h3>
          <Empty description="डेटा उपलब्ध नाही" />
        </div>
      );
    }

    const chartData = data.map((item) => ({
      name: item.district_name,
      value: Number(item.accused_count || 0),
    }));

    const renderCustomLabel = ({
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      value,
    }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      return (
        <text
          x={x}
          y={y}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={13}
          fontWeight={700}
        >
          {value}
        </text>
      );
    };

    return (
      <div
        style={{
          width: "100%",
          padding: 24,
          border: "1px solid #e8e8e8",
          borderRadius: 12,
          background: "#fafcff",
        }}
      >
        <h3
          style={{
            textAlign: "center",
            marginBottom: 16,
            color: "#1890ff",
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          {title}
        </h3>
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="45%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius="70%"
              innerRadius="30%"
              dataKey="value"
              stroke="#fff"
              strokeWidth={3}
            >
              {chartData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={PIE_COLORS[index % PIE_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [value, name]}
              contentStyle={{ borderRadius: 8, fontSize: 13 }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              iconSize={12}
              wrapperStyle={{ paddingTop: 16 }}
              formatter={(value, entry) => (
                <span style={{ color: "#333", fontSize: 13, fontWeight: 500 }}>
                  {value} ({entry.payload.value})
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  renderSingleBarSection = (
    data,
    title,
    chartExportId = "admin-nd-chart-modus",
  ) => {
    if (!data || data.length === 0) {
      return (
        <div style={{ width: "100%", textAlign: "center", padding: 24 }}>
          <h3 style={{ marginBottom: 12, color: "#1890ff" }}>{title}</h3>
          <Empty description="डेटा उपलब्ध नाही" />
        </div>
      );
    }

    const chartData = data.map((item) => ({
      name: item.district_name,
      count: Number(item.accused_count || 0),
    }));

    return (
      <div
        className="admin-nd-chart-card"
        style={{
          width: "100%",
          border: "1px solid #e8e8e8",
          borderRadius: 12,
          background: "#fafcff",
          overflow: "hidden",
        }}
      >
        <div className="admin-nd-chart-card__toolbar">
          <h3 className="admin-nd-chart-card__title">{title}</h3>
          <div className="admin-nd-chart-card__actions">
            {this.renderChartDownloadButton(chartExportId, "modus_bar")}
          </div>
        </div>
        <div
          id={chartExportId}
          className="admin-nd-chart-card__body admin-nd-chart-export-panel"
        >
          <p className="admin-nd-chart-export-title">{title}</p>
          <ResponsiveContainer width="100%" height={380}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                interval={0}
                angle={-25}
                textAnchor="end"
                height={60}
              />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value, name) => [
                  value,
                  name === "count" ? "Count" : name,
                ]}
              />
              <Legend />
              <Bar
                dataKey="count"
                name="Count"
                fill="#7c3aed"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  handleDistrictChartLimitChange = (limit) => {
    this.setState({ detailDistrictChartLimit: limit });
  };

  getMovementAnalysisFileBase = () => {
    const { detailModalType, detailSelectedDistrictName } = this.state;
    const cfg = MOVEMENT_MODAL_CONFIG[detailModalType] || {};
    const typePart = (cfg.titleEn || detailModalType || "movement")
      .replace(/[^\w]+/g, "_")
      .replace(/_+/g, "_");
    const districtPart = detailSelectedDistrictName
      ? `_${String(detailSelectedDistrictName).replace(/[^\w]+/g, "_")}`
      : "";
    return `CMIS_${typePart}${districtPart}`;
  };

  downloadChartAsImage = (elementId, labelSuffix) => {
    const node = document.getElementById(elementId);
    if (!node) {
      message.warning("चार्ट सापडला नाही");
      return;
    }
    const fileName = `${this.getMovementAnalysisFileBase()}_${labelSuffix}_${moment().format("DDMMMYYYY")}.png`;
    domtoimage
      .toPng(node, {
        bgcolor: "#fafcff",
        cacheBust: true,
        style: {
          transform: "scale(1)",
          transformOrigin: "top left",
        },
      })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = fileName;
        link.href = dataUrl;
        link.click();
      })
      .catch(() => {
        message.error("चित्र डाउनलोड अयशस्वी");
      });
  };

  downloadCrimeListExcel = () => {
    const {
      detailDistrictAccusedList,
      detailSelectedDistrictName,
      detailModalType,
      customDates,
    } = this.state;
    const { user } = this.props;
    const { t } = this.context;

    if (
      !Array.isArray(detailDistrictAccusedList) ||
      detailDistrictAccusedList.length === 0
    ) {
      message.warning("डाउनलोड करण्यासाठी गुन्ह्यांची यादी उपलब्ध नाही");
      return;
    }

    const cfg = MOVEMENT_MODAL_CONFIG[detailModalType] || {};
    const movementLabel = cfg.titleMr
      ? `${cfg.titleMr} (${cfg.titleEn})`
      : detailModalType || "Movement";
    const districtLabel = detailSelectedDistrictName || "-";
    const reportTitle = `Crime List — ${movementLabel} — ${districtLabel}`;
    const file_name = `${this.getMovementAnalysisFileBase()}_crime_list_${moment().format("DDMMMYYYY")}`;

    const arrData = detailDistrictAccusedList.map((row, index) => ({
      [t("sr.no")]: row.sr_no ?? index + 1,
      [t("name")]: row.name ? row.name : "-",
      [t("address")]: row.bi_district_display ? row.bi_district_display : "-",
      "Crime District": row.gi_district_display ? row.gi_district_display : "-",
      [t("total_crimes")]: row.total_count != null ? row.total_count : "-",
      [t("crime_heading")]: row.crime_heading_name
        ? row.crime_heading_name
        : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(arrData, { origin: "A4" });

    const columnWidths = Object.keys(arrData[0]).map((key) => ({
      wch:
        Math.max(
          key.length,
          ...arrData.map((r) => String(r[key] || "").length),
        ) + 6,
    }));
    ws["!cols"] = columnWidths;

    const titleStyle = {
      font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1F4E78" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    const subTitleStyle = {
      font: { bold: true, sz: 12, color: { rgb: "1F4E78" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "305496" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    const cellStyle = {
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    Object.keys(arrData[0]).forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 3, c: colIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = headerStyle;
      }
    });

    arrData.forEach((row, rowIndex) => {
      Object.keys(row).forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({
          r: rowIndex + 4,
          c: colIndex,
        });
        if (ws[cellRef]) {
          ws[cellRef].s = cellStyle;
        }
      });
    });

    const metrics = this.getMetrics();
    const list = this.state.list || {};
    const districtName =
      list.district_name || list.districtName || list.DistrictName || "";
    const headerData = getReportHeaderText({
      user,
      cityName: metrics.cityName,
      districtName,
    });

    ws["A1"] = { v: headerData || metrics.cityName || "CMIS", t: "s" };
    ws["A1"].s = titleStyle;

    ws["A2"] = { v: reportTitle, t: "s" };
    ws["A2"].s = subTitleStyle;

    ws["A3"] = {
      v: getReportDateRangeText(customDates?.[0], customDates?.[1]) || "-",
      t: "s",
    };
    ws["A3"].s = subTitleStyle;

    const footerContent = getDefaultReportFooter();
    const footerRowIndex = arrData.length + 6;

    XLSX.utils.sheet_add_aoa(ws, [[footerContent]], {
      origin: `A${footerRowIndex}`,
    });
    ws[`A${footerRowIndex}`].s = {
      font: { italic: true, sz: 11, color: { rgb: "555555" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    const colSpan = columnWidths.length - 1;
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colSpan } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colSpan } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: colSpan } },
      {
        s: { r: footerRowIndex - 1, c: 0 },
        e: { r: footerRowIndex - 1, c: colSpan },
      },
    ];

    ws["!rows"] = Array(footerRowIndex).fill({ hpt: 25 });
    ws["!rows"][footerRowIndex - 1] = { hpt: 40 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${file_name}.xlsx`);
  };

  renderChartDownloadButton = (elementId, labelSuffix) => (
    <AntTooltip title="Download chart as PNG">
      <Button
        type="default"
        size="small"
        className="admin-nd-btn admin-nd-btn--png"
        icon={<DownloadOutlined />}
        onClick={() => this.downloadChartAsImage(elementId, labelSuffix)}
      >
        PNG
      </Button>
    </AntTooltip>
  );

  renderExcelDownloadButton = () => (
    <AntTooltip title="Download crime list as Excel">
      <Button
        type="primary"
        size="small"
        className="admin-nd-btn admin-nd-btn--excel"
        icon={<FileExcelOutlined />}
        onClick={this.downloadCrimeListExcel}
      >
        Excel
      </Button>
    </AntTooltip>
  );

  renderCrimeListPlaceholder = (chartMode = "pie") => {
    const isLine = chartMode === "line";
    const ChartIcon = isLine ? LineChartOutlined : PieChartOutlined;
    return (
      <div className="admin-nd-crime-list-hint" role="note">
        <div className="admin-nd-crime-list-hint__badge">
          <InfoCircleOutlined />
          <span>सूचना / Note</span>
        </div>
        <div className="admin-nd-crime-list-hint__body">
          <div className="admin-nd-crime-list-hint__icon-wrap">
            <ChartIcon />
          </div>
          <div className="admin-nd-crime-list-hint__text">
            <p className="admin-nd-crime-list-hint__title-mr">
              {isLine
                ? "गुन्ह्यांची यादी पाहण्यासाठी वरील लाइन चार्टवरील कोणत्याही पोलीस ठाणे क्लिक करा."
                : "गुन्ह्यांची यादी पाहण्यासाठी वरील पाई चार्टमधील कोणत्याही पोलीस ठाणे विभागावर क्लिक करा."}
            </p>
            <p className="admin-nd-crime-list-hint__title-en">
              {isLine
                ? "Click any district on the line chart above to view the crime list for that district."
                : "Click any district slice on the pie chart above to view the crime list for that district."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  handleDistrictChartSliceClick = (districtId, districtName) => {
    if (districtId == null) return;
    this.setState({ detailSelectedDistrictName: districtName ?? null }, () =>
      this.fetchMovementModusPie({ districtId }),
    );
  };

  renderDistrictLimitDropdown = () => {
    const { detailDistrictChartLimit } = this.state;
    return (
      <label className="admin-nd-district-limit-select-wrap">
        <span className="admin-nd-district-limit-select-label">कालावधी</span>
        <select
          className="admin-nd-district-limit-select"
          value={detailDistrictChartLimit}
          onChange={(e) => this.handleDistrictChartLimitChange(e.target.value)}
        >
          <option value="5">Top 5</option>
          <option value="10">Top 10</option>
          <option value="all">All</option>
        </select>
      </label>
    );
  };

  renderPieChartLabel = () => {
    return ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      return (
        <text
          x={x}
          y={y}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={13}
          fontWeight={700}
        >
          {value}
        </text>
      );
    };
  };

  getPieDistrictRows = (districtRows, limit) => {
    if (limit === "all") return [];
    return districtRows.slice(0, limit === "10" ? 10 : 5);
  };

  renderCurrentPieChart = () => {
    const {
      detailTop5,
      detailDistrictChartLimit,
      detailModusPie,
      detailModalType,
      detailSelectedDistrictId,
      detailSelectedDistrictName,
      detailDistrictAccusedList,
    } = this.state;
    const cfg = MOVEMENT_MODAL_CONFIG[detailModalType] || {};
    const title = cfg.titleMr ? `${cfg.titleMr} (${cfg.titleEn})` : "";

    const districtRows = Array.isArray(detailTop5) ? detailTop5 : [];
    const modusRows = Array.isArray(detailModusPie) ? detailModusPie : [];
    const showAllDistrictsLine = detailDistrictChartLimit === "all";
    const pieDistrictRows = this.getPieDistrictRows(
      districtRows,
      detailDistrictChartLimit,
    );

    const mapDistrictToChart = (item) => ({
      name: item.district_name,
      value: Number(item.accused_count || 0),
      district_id: item.district_id,
    });

    const districtChartData = pieDistrictRows.map(mapDistrictToChart);
    const lineChartData = districtRows.map(mapDistrictToChart);
    const renderCustomLabel = this.renderPieChartLabel();

    const modusChartData = modusRows.map((r) => ({
      district_name: r.modus_name ?? r.district_name,
      accused_count: r.accused_count,
    }));

    const modusBarTitle =
      detailSelectedDistrictId != null
        ? `Modus Operandi — ${detailSelectedDistrictName || "Selected District"}`
        : "Modus Operandi — All District";

    const panelStyle = {
      width: "100%",
      padding: 24,
      border: "1px solid #e8e8e8",
      borderRadius: 12,
      background: "#fafcff",
    };

    return (
      <div className="admin-nd-analysis-layout">
        {/* Row 1: District pie (5/10) or line chart (All) + dropdown */}
        <section className="admin-nd-analysis-row admin-nd-analysis-row--pie">
          <div
            className="admin-nd-chart-card"
            style={{ ...panelStyle, padding: 0, overflow: "hidden" }}
          >
            <div className="admin-nd-chart-card__toolbar">
              <h3 className="admin-nd-chart-card__title">({title})</h3>
              <div className="admin-nd-chart-card__actions">
                <div className="admin-nd-toolbar-group admin-nd-toolbar-group--filter">
                  {this.renderDistrictLimitDropdown()}
                </div>
                <span className="admin-nd-toolbar-divider" aria-hidden="true" />
                <div className="admin-nd-toolbar-group">
                  <AntTooltip title="Clear district filter">
                    <Button
                      size="small"
                      className="admin-nd-btn admin-nd-btn--ghost"
                      onClick={() =>
                        this.setState(
                          { detailSelectedDistrictName: null },
                          () =>
                            this.fetchMovementModusPie({ districtId: null }),
                        )
                      }
                    >
                      Reset
                    </Button>
                  </AntTooltip>
                  {(showAllDistrictsLine
                    ? lineChartData.length > 0
                    : districtChartData.length > 0) &&
                    this.renderChartDownloadButton(
                      "admin-nd-chart-district",
                      showAllDistrictsLine ? "district_line" : "district_pie",
                    )}
                </div>
              </div>
            </div>
            <div
              id="admin-nd-chart-district"
              className="admin-nd-district-chart-panel admin-nd-chart-card__body admin-nd-chart-export-panel"
            >
              <p className="admin-nd-chart-export-title">({title})</p>
              {showAllDistrictsLine ? (
                lineChartData.length === 0 ? (
                  <Empty description="डेटा उपलब्ध नाही" />
                ) : (
                  <ResponsiveContainer width="100%" height={360}>
                    <LineChart
                      data={lineChartData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        interval={0}
                        angle={-25}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        formatter={(value) => [value, "Count"]}
                        contentStyle={{ borderRadius: 8, fontSize: 13 }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="Count"
                        stroke="#1890ff"
                        strokeWidth={2}
                        dot={{ r: 4, strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                        onClick={(data) => {
                          const did = data?.payload?.district_id;
                          const dname = data?.payload?.name;
                          this.handleDistrictChartSliceClick(did, dname);
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )
              ) : districtChartData.length === 0 ? (
                <Empty description="डेटा उपलब्ध नाही" />
              ) : (
                <ResponsiveContainer width="100%" height={360}>
                  <PieChart>
                    <Pie
                      data={districtChartData}
                      cx="50%"
                      cy="45%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius="70%"
                      innerRadius="30%"
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={3}
                      onClick={(data) => {
                        const did = data?.payload?.district_id;
                        const dname = data?.payload?.name;
                        this.handleDistrictChartSliceClick(did, dname);
                      }}
                    >
                      {districtChartData.map((row, index) => (
                        <Cell
                          key={`district-cell-${row.district_id ?? index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                          opacity={
                            detailSelectedDistrictId != null &&
                            row.district_id !== detailSelectedDistrictId
                              ? 0.45
                              : 1
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ borderRadius: 8, fontSize: 13 }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      iconSize={12}
                      wrapperStyle={{ paddingTop: 16 }}
                      formatter={(value, entry) => (
                        <span
                          style={{
                            color: "#333",
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          {value} ({entry.payload.value})
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* Row 2: Modus bar chart */}
        <section className="admin-nd-analysis-row admin-nd-analysis-row--bar">
          {this.renderSingleBarSection(modusChartData, modusBarTitle)}
        </section>

        {/* Row 3: Crime details */}
        <section className="admin-nd-analysis-row admin-nd-analysis-row--crime">
          <div style={{ ...panelStyle, background: "#fff" }}>
            <div className="admin-nd-chart-card__toolbar admin-nd-chart-card__toolbar--crime">
              <h3 className="admin-nd-chart-card__title">
                Crime List
                {detailSelectedDistrictName ? (
                  <span className="admin-nd-chart-card__subtitle">
                    {" "}
                    — {detailSelectedDistrictName}
                  </span>
                ) : null}
              </h3>
              <span className="admin-nd-detail-modal__total-badge">
                एकूण: <strong>{detailDistrictAccusedList.length}</strong>
              </span>
              {detailSelectedDistrictId != null &&
              detailDistrictAccusedList.length > 0
                ? this.renderExcelDownloadButton()
                : null}
            </div>

            {detailSelectedDistrictId != null ? (
              detailDistrictAccusedList.length > 0 ? (
                <Table
                  rowKey={(r) => String(r.masterId ?? r.sr_no)}
                  size="small"
                  bordered
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "15", "20", "50"],
                  }}
                  dataSource={detailDistrictAccusedList}
                  columns={[
                    {
                      title: "अ.क्र.",
                      dataIndex: "sr_no",
                      width: 70,
                      align: "center",
                    },
                    {
                      title: "Name",
                      dataIndex: "name",
                      width: 200,
                      render: (text, record) => (
                        <Button
                          type="link"
                          title={record.masterId}
                          onClick={() => {
                            this.onViewAccusedForm(record.masterId);
                          }}
                        >
                          {record.name}
                        </Button>
                      ),
                    },
                    {
                      title: "Residence (PS/District)",
                      dataIndex: "bi_district_display",
                      width: 220,
                    },
                    {
                      title: "Crime District",
                      dataIndex: "gi_district_display",
                      width: 220,
                    },
                    {
                      title: "Crime Count",
                      dataIndex: "total_count",
                      width: 110,
                      align: "center",
                      render: (text, record) => (
                        <div>
                          <Button
                            type="primary"
                            shape="circle"
                            size="small"
                            style={{
                              width: 36,
                              height: 36,
                              fontWeight: 600,
                              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            }}
                            onClick={() => this.onGunaForm(record.masterId)}
                          >
                            {record.total_count}
                          </Button>
                        </div>
                      ),
                    },
                    {
                      title: "Heading",
                      dataIndex: "crime_heading_name",
                      width: 200,
                    },
                  ]}
                  scroll={{ x: 1100 }}
                />
              ) : (
                <Empty
                  description={
                    detailSelectedDistrictName
                      ? `${detailSelectedDistrictName} — या जिल्ह्यासाठी गुन्ह्यांची यादी उपलब्ध नाही`
                      : "या जिल्ह्यासाठी गुन्ह्यांची यादी उपलब्ध नाही"
                  }
                />
              )
            ) : (
              this.renderCrimeListPlaceholder(
                showAllDistrictsLine ? "line" : "pie",
              )
            )}
          </div>
        </section>
      </div>
    );
  };

  renderMovementDetailModalTitle = (cfg, accusedList, dateRangeText) => {
    const titleText = cfg.titleMr
      ? `${cfg.titleMr} (${cfg.titleEn})`
      : "हालचाल तपशील";
    const totalCount = formatNumber(accusedList.length);

    return (
      <div className="admin-nd-detail-modal__title">
        <span className="admin-nd-detail-modal__title-text">{titleText}</span>
        <div className="admin-nd-detail-modal__title-meta">
          {dateRangeText ? (
            <span className="admin-nd-detail-modal__date-range">
              <CalendarOutlined />
              {dateRangeText}
            </span>
          ) : null}
          <span className="admin-nd-detail-modal__total-badge">
            एकूण: <strong>{totalCount}</strong>
          </span>
        </div>
      </div>
    );
  };

  renderMovementDetailModal = () => {
    const {
      detailModalVisible,
      detailModalType,
      detailModalView,
      detailLoading,
      detailAccusedList,
      movementDetailPagination,
      customDates,
    } = this.state;
    const { t } = this.context;
    const cfg = MOVEMENT_MODAL_CONFIG[detailModalType] || {};
    const accusedList = detailAccusedList;
    const dateRangeText = formatDateRange(customDates);
    const getPreventiveStatusKey = (record) => {
      if (
        !record.preventive_records ||
        record.preventive_records.length === 0
      ) {
        return "-";
      }
      const { status } = record.preventive_records[0] || {};
      return status === "completed" || status === "ongoing" ? status : "-";
    };
    const movementDetailColumns = [
      {
        title: "अ.क्र.",
        dataIndex: "sr_no",
        width: 70,
        align: "center",
      },
      {
        title: "आरोपीचे नाव",
        dataIndex: "name",
        render: (text, record) => (
          <Button
            type="link"
            title={record.masterId}
            onClick={() => {
              this.onViewAccusedForm(record.masterId);
            }}
          >
            {record.name}
          </Button>
        ),
        width: 180,
      },
      {
        title: "पो.स्टे / घटक  .",
        dataIndex: "bi_district_name",
        sorter: (a, b) => a.bi_district_name.localeCompare(b.bi_district_name),
        width: 200,
        render: (_, row) =>
          formatDistrictWithCity(row.bi_district_name, row.bi_city_name),
      },
      {
        title: "पो.स्टे./ गुन्ह्याचे जिल्हा ",
        dataIndex: "gi_district_name",
        sorter: (a, b) => a.gi_district_name.localeCompare(b.gi_district_name),
        width: 200,
        render: (_, row) =>
          formatDistrictWithCity(row.gi_district_name, row.gi_city_name),
      },
      // {
      //   title: "राहण्याचे शहर",
      //   dataIndex: "bi_city_name",
      //   width: 130,
      // },
      // {
      //   title: "गुन्ह्याचे शहर",
      //   dataIndex: "gi_city_name",
      //   width: 130,
      // },
      {
        title: "एकूण गुन्हे",
        dataIndex: "total_count",
        sorter: (a, b) => a.total_count - b.total_count,
        width: 100,
        align: "center",
        // render: (v) => formatNumber(v),
        render: (text, record) => (
          <div>
            <Button
              type="primary"
              shape="circle"
              size="small"
              style={{
                width: 36,
                height: 36,
                fontWeight: 600,
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              }}
              onClick={() => this.onGunaForm(record.masterId)}
            >
              {record.total_count}
            </Button>
          </div>
        ),
      },
      {
        title: "शेवटचा गुन्हा दिनांक",
        sorter: (a, b) => a.crime_date.localeCompare(b.crime_date),
        dataIndex: "crime_date",
        width: 120,
        align: "center",
      },
      {
        title: t("preventive_action"),
        key: "preventive_records",
        width: 120,
        align: "center",
        filters: [
          { text: t("completed"), value: "completed" },
          { text: t("ongoing"), value: "ongoing" },
          { text: t("not_available"), value: "-" },
        ],
        onFilter: (value, record) => getPreventiveStatusKey(record) === value,
        sorter: (a, b) => {
          const order = { completed: 0, ongoing: 1, "-": 2 };
          return (
            order[getPreventiveStatusKey(a)] - order[getPreventiveStatusKey(b)]
          );
        },
        onHeaderCell: () => ({
          style: {
            color: "#000",
            fontWeight: "bold",
          },
        }),

        render: (text, record) => {
          const statusKey = getPreventiveStatusKey(record);

          if (statusKey === "-") {
            return {
              props: { style: { color: "#000" } },
              children: <div className="action-links-responsive">-</div>,
            };
          }

          const statusLabel = t(statusKey);
          const statusColor =
            statusKey === "completed"
              ? "rgb(250, 173, 20)"
              : statusKey === "ongoing"
                ? "rgb(82, 196, 26)"
                : "#000";

          return {
            props: { style: { color: statusColor } },
            children: (
              <div className="action-links-responsive">
                <span
                  className={`preventive-status-label preventive-link-${statusKey}`}
                  style={{ color: statusColor }}
                >
                  {statusLabel}
                </span>
              </div>
            ),
          };
        },
      },
    ];

    return (
      <Modal
        centered
        title={this.renderMovementDetailModalTitle(
          cfg,
          accusedList,
          dateRangeText,
        )}
        visible={detailModalVisible}
        onCancel={this.closeMovementDetailModal}
        footer={null}
        width={95 + "%"}
        destroyOnClose
        className="admin-nd-detail-modal"
        bodyStyle={{
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            marginBottom: 20,
            display: "flex",
            justifyContent: "flex-end",
            paddingRight: 8,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              borderRadius: 30,
              border: "3px solid #7c3aed",
              padding: 3,
              background: "#fff",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <div
              onClick={() => this.setState({ detailModalView: "list" })}
              style={{
                padding: "8px 24px",
                borderRadius: 26,
                fontWeight: 600,
                fontSize: 14,
                background: detailModalView === "list" ? "#7c3aed" : "#fff",
                color: detailModalView === "list" ? "#fff" : "#7c3aed",
                transition: "all 0.3s ease",
              }}
            >
              यादी (List)
            </div>
            <div
              onClick={() => this.setState({ detailModalView: "analysis" })}
              style={{
                padding: "8px 24px",
                borderRadius: 26,
                fontWeight: 600,
                fontSize: 14,
                background: detailModalView === "analysis" ? "#7c3aed" : "#fff",
                color: detailModalView === "analysis" ? "#fff" : "#7c3aed",
                transition: "all 0.3s ease",
              }}
            >
              विश्लेषण (Analysis)
            </div>
          </div>
        </div>

        <div className="cmis-loader-scope">
          {detailLoading && this.renderCmisLoader({ variant: "modal" })}
          {detailModalView === "list" ? (
            accusedList.length > 0 ? (
              <Table
                rowKey={(r) => String(r.masterId ?? r.sr_no)}
                columns={movementDetailColumns}
                dataSource={accusedList}
                pagination={{
                  current: movementDetailPagination.current,
                  pageSize: movementDetailPagination.pageSize,
                  total: accusedList.length,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "15", "20", "50"],
                  hideOnSinglePage: true,
                }}
                onChange={this.handleMovementDetailTableChange}
                scroll={{ x: 960, y: 420 }}
                bordered
                size="small"
              />
            ) : (
              !detailLoading && <Empty description="डेटा उपलब्ध नाही" />
            )
          ) : (
            this.renderCurrentPieChart()
          )}
        </div>
      </Modal>
    );
  };

  render() {
    const {
      dateFilter,
      customDates,
      loading,
      advancedFilterTitle,
      selectedHeadings,
      crimeTitle,
      main_head,
    } = this.state;
    const { professionals } = this.props;
    const { pc_crimetitles, pc_crimetypes } = professionals || {};
    const metrics = this.getMetrics();
    const dateRangeText = formatDateRange(customDates);
    const {
      totalAccused,
      localCount,
      internalCount,
      externalIncoming,
      externalOutgoing,
      totalPoliceStations,
      totalCrimes,
      cityName,
      titleCount,
    } = metrics;
    const { t } = this.context;

    const psLabel = totalPoliceStations
      ? formatNumber(totalPoliceStations)
      : titleCount;

    return (
      <div className="admin-nd-page">
        <style>{CMIS_LOADER_STYLES}</style>
        {loading && this.renderCmisLoader()}
        <header className="admin-nd-header">
          <div className="admin-nd-header__brand">
            <div className="admin-nd-logo">
              <SafetyOutlined />
            </div>
          </div>

          <div className="admin-nd-header__title-block">
            <h1 className="admin-nd-title">
              {psLabel !== "—" ? `${psLabel} ` : ""}पोलीस स्टेशन गुन्हेगार
              हालचाल विश्लेषण
            </h1>
            <p className="admin-nd-subtitle">
              Local, Internal Movement, External Incoming &amp; External
              Outgoing आरोपीचे एकत्रित विश्लेषण
              {cityName && !this.isMappedCityRole() ? ` — ${cityName}` : ""}
            </p>
            {this.isMappedCityRole() && cityName ? (
              <div className="admin-nd-city-highlight">
                <span>निवडलेले शहर</span>
                <strong>{cityName}</strong>
              </div>
            ) : null}
          </div>

          <div className="admin-nd-header__actions">
            <div
              role="button"
              tabIndex={0}
              className="admin-nd-ps-badge admin-nd-ps-badge--clickable"
              onClick={this.openPsDistrictModal}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  this.openPsDistrictModal();
                }
              }}
              title="पोलीस स्टेशन यादी पाहण्यासाठी क्लिक करा"
            >
              <BankOutlined />
              <span>
                एकूण पो.स्टे. <strong>{psLabel}</strong>
              </span>
            </div>
          </div>
        </header>

        <section className="admin-nd-filters">
          <div className="admin-nd-filter-grid">
            {this.isMappedCityRole() && (
              <div className="admin-nd-filter-field">
                <div className="admin-nd-filter-field__inner">
                  <label>शहर निवडा</label>
                  <Select
                    className="admin-nd-city-select"
                    placeholder="Select City"
                    value={this.state.selectedCityId}
                    onChange={this.handleMappedCityChange}
                    showSearch
                    optionFilterProp="children"
                    style={{ width: "100%" }}
                  >
                    {this.getAllocatedCities().map((city) => (
                      <Option key={city.id} value={city.id}>
                        {city.name}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            <div className="admin-nd-filter-field">
              <div className="admin-nd-filter-field__inner">
                <label>शीर्षक / लघुशीर्षक / कायदा-कलम निवडा</label>
                <Select
                  placeholder="Select Title"
                  onChange={this.handleAdvancedFilterTitleChange}
                  value={advancedFilterTitle}
                  style={{ width: "100%" }}
                >
                  <Option value="शीर्षक">{t("title")}</Option>
                  <Option value="लघु शीर्षक">{t("sub_title")}</Option>
                  <Option value="मोडस">{t("modus_operandi")}</Option>
                  <Option value="कायदा">{`${t("law")} / ${t("section")}`}</Option>
                </Select>
              </div>
            </div>

            {advancedFilterTitle === "शीर्षक" && (
              <div className="admin-nd-filter-field">
                <div className="admin-nd-filter-field__inner">
                  <label>शीर्षक निवडा</label>
                  <Select
                    mode="multiple"
                    placeholder="Please select Crime Type"
                    value={selectedHeadings}
                    onChange={(val) => this.setState({ selectedHeadings: val })}
                    showSearch
                    maxTagCount={1}
                    style={{ width: "100%" }}
                  >
                    {pc_crimetitles?.map((res) => (
                      <Option key={res.id} value={res.id}>
                        {res.crime_title}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {advancedFilterTitle === "लघु शीर्षक" && (
              <div className="admin-nd-filter-field">
                <div className="admin-nd-filter-field__inner">
                  <label>लघु शीर्षक निवडा</label>
                  <Select
                    mode="multiple"
                    placeholder="Please select Crime Type"
                    value={crimeTitle}
                    onChange={(val) => this.setState({ crimeTitle: val })}
                    showSearch
                    maxTagCount={1}
                    style={{ width: "100%" }}
                  >
                    {pc_crimetypes?.map((res) => (
                      <Option key={res.id} value={res.id}>
                        {res.crimetype}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {advancedFilterTitle === "मोडस" && (
              <div className="admin-nd-filter-field">
                <div className="admin-nd-filter-field__inner">
                  <label>मोडस निवडा</label>
                  <Select
                    mode="multiple"
                    allowClear
                    value={crimeTitle}
                    onChange={this.handleCrimeChange}
                    style={{ width: "100%" }}
                    placeholder="Please select option"
                    optionFilterProp="children"
                    maxTagCount={1}
                    showSearch
                  >
                    <Option value="all">All</Option>
                    {main_head?.map((item) => (
                      <Option
                        key={String(item.title_id)}
                        value={String(item.title_id)}
                      >
                        {item.main_head_type}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {advancedFilterTitle === "कायदा" && (
              <div className="admin-nd-filter-field">
                <div className="admin-nd-filter-field__inner">
                  <label>कायदा निवडा</label>
                  <Select
                    allowClear
                    placeholder={t("please_select_an_option")}
                    value={this.state.kayda}
                    onChange={(val) => this.handleKala(val)}
                    showSearch
                    style={{ width: "100%" }}
                  >
                    {professionals?.pc_acts?.map((item) => (
                      <Option key={item.act_cd} value={item.act_cd}>
                        {item.kayda}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {advancedFilterTitle === "कायदा" && this.state.kayda && (
              <div className="admin-nd-filter-field">
                <div className="admin-nd-filter-field__inner">
                  <label>कलम निवडा</label>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Please select option"
                    value={this.state.kalam}
                    onChange={(val) => this.setState({ kalam: val })}
                    showSearch
                    maxTagCount={1}
                    style={{ width: "100%" }}
                  >
                    {professionals?.pc_kalams?.map((item) => (
                      <Option key={item.section} value={item.section}>
                        {item.section}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            <div className="admin-nd-filter-field admin-nd-filter-field--period">
              <div className="admin-nd-filter-field__inner">
                <label>कालावधी निवडा</label>
                <Select
                  value={dateFilter}
                  style={{ width: "100%" }}
                  onChange={this.handleDateFilterChange}
                >
                  <Option value="1month">1 Month</Option>
                  <Option value="6month">6 Months</Option>
                  <Option value="1year">1 Year</Option>
                  <Option value="custom">Custom Date</Option>
                </Select>
              </div>
            </div>

            <div className="admin-nd-filter-field admin-nd-filter-field--daterange">
              <RangePicker
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
                value={customDates}
                onChange={(dates) =>
                  this.setState({
                    customDates: dates,
                  })
                }
                disabled={dateFilter !== "custom"}
              />
            </div>

            <div className="admin-nd-filter-field admin-nd-filter-field--submit">
              <Button
                type="primary"
                disabled={loading}
                onClick={this.fetchDashboard}
              >
                Find
              </Button>
            </div>
          </div>
        </section>

        <section className="admin-nd-hub-section">
          <div className="admin-nd-hub-layout">
            {this.renderSectionCard("local", metrics, totalAccused, psLabel)}
            {this.renderSectionCard("internal", metrics, totalAccused, psLabel)}

            <div className="admin-nd-hub-center">
              <div className="admin-nd-hub-aura" aria-hidden="true" />
              <div
                className="admin-nd-hub-ring admin-nd-hub-ring--reverse"
                aria-hidden="true"
              />
              <div className="admin-nd-hub-ring" aria-hidden="true">
                <span className="admin-nd-dot admin-nd-dot--blue" />
                <span className="admin-nd-dot admin-nd-dot--green" />
                <span className="admin-nd-dot admin-nd-dot--purple" />
                <span className="admin-nd-dot admin-nd-dot--orange" />
              </div>
              <div className="admin-nd-hub-sweep" aria-hidden="true" />
              <div className="admin-nd-hub-core">
                <BankOutlined className="admin-nd-hub-core__icon" />
                <p className="admin-nd-hub-core__label">एकूण आरोपी</p>
                <h2 className="admin-nd-hub-core__value">
                  {formatNumber(totalAccused)}
                </h2>
                <p className="admin-nd-hub-core__hint">
                  {psLabel !== "—" ? `${psLabel} ` : ""}पोलीस स्टेशन हद्दीतील
                  एकत्रित आकडेवारी
                </p>
              </div>
            </div>

            {this.renderSectionCard(
              "external_incoming",
              metrics,
              totalAccused,
              psLabel,
            )}
            {this.renderSectionCard(
              "external_outgoing",
              metrics,
              totalAccused,
              psLabel,
            )}
          </div>
        </section>

        <footer className="admin-nd-footer">
          <div className="admin-nd-footer__item">
            <TeamOutlined />
            <div>
              <span>एकूण आरोपी</span>
              <strong>{formatNumber(totalAccused)}</strong>
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="admin-nd-footer__item admin-nd-footer__item--clickable"
            onClick={this.openPsDistrictModal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                this.openPsDistrictModal();
              }
            }}
            title="पोलीस स्टेशन यादी"
          >
            <BankOutlined />
            <div>
              <span>एकूण पो.स्टे.</span>
              <strong>{psLabel}</strong>
            </div>
          </div>
          <div className="admin-nd-footer__item">
            <FileProtectOutlined />
            <div>
              <span>एकूण गुन्हे</span>
              <strong>{formatNumber(totalCrimes)}</strong>
            </div>
          </div>
          <div className="admin-nd-footer__item">
            <CalendarOutlined />
            <div>
              <span>निवडलेली कालावधी</span>
              <strong>{dateRangeText}</strong>
            </div>
          </div>
        </footer>

        {this.renderMovementDetailModal()}
        {this.renderPsDistrictModal()}
        <Modal
          title="आरोपी प्रोफाइल"
          top
          visible={this.state.setCriminalViewModel}
          onOk={this.closeCriminalViewModel}
          onCancel={this.closeCriminalViewModel}
          width={95 + "%"}
        >
          <ViewFormModel
            key={this.props && this.props.user.id}
            dataStates={this.props.professionals}
            preventiveAction={this.state.preventiveAction}
          ></ViewFormModel>
        </Modal>
        <Modal
          title={t("crime_information")}
          top
          visible={this.state.showGunaDetails}
          onOk={this.closeGunaViewModel}
          onCancel={this.closeGunaViewModel}
          width={95 + "%"}
        >
          <ViewGunaDetails
            pc_crimetitles={professionals?.pc_crimetitles}
            dataStates={
              Array.isArray(professionals?.criminalViewList?.gunhaInfor) &&
              professionals?.criminalViewList?.gunhaInfor.length > 0
                ? professionals?.criminalViewList?.gunhaInfor
                : []
            }
            name={
              Array.isArray(professionals?.criminalViewList?.basicInfo) &&
              professionals?.criminalViewList?.basicInfo[0]
                ? professionals?.criminalViewList?.basicInfo[0].name
                : ""
            }
            address={
              Array.isArray(professionals?.criminalViewList?.basicInfo) &&
              professionals?.criminalViewList?.basicInfo[0]
                ? professionals?.criminalViewList?.basicInfo[0].address
                : ""
            }
            loading={this.state.gunaLoading}
            user_details={this.props?.user}
            masterId={
              Array.isArray(professionals?.criminalViewList?.basicInfo) &&
              professionals?.criminalViewList?.basicInfo[0]
                ? professionals?.criminalViewList?.basicInfo[0].id
                : ""
            }
            closeGunaViewModel={this.closeGunaViewModel}
          ></ViewGunaDetails>
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { users, authentication, professionals } = state;
  const { user } = authentication;
  return {
    user,
    professionals,
  };
}

const ConnectedAdminNewDashboard = connect(mapStateToProps)(AdminNewDashboard);
export { ConnectedAdminNewDashboard as AdminNewDashboard };
